/**
 * Header "Download app" button — replaces the former global install popup.
 *
 * Android/Chrome triggers the real install sheet; iPhone Safari shows the
 * Share > Add to Home Screen instructions, since Apple offers no install API.
 * Elsewhere (desktop, preview contexts) it opens the same short instructions
 * so the button is never a dead end. Hidden only in installed (standalone)
 * mode, where the app is already on the home screen.
 */
import { useEffect, useRef, useState } from "react";
import { Share, Smartphone, X } from "lucide-react";
import {
  getInstallCapability,
  isStandalone,
  promptInstall,
  subscribeInstallState,
  type InstallCapability,
} from "@/lib/pwa/install";
import { trackInstallPrompt } from "@/lib/pwa/telemetry";

export function DownloadAppButton() {
  const [cap, setCap] = useState<InstallCapability>("none");
  const [ready, setReady] = useState(false);
  const [installed, setInstalled] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const helpRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const sync = () => {
      setInstalled(isStandalone());
      setCap(getInstallCapability());
      setReady(true);
    };
    sync();
    const unsubscribe = subscribeInstallState(sync);
    const standaloneQuery = window.matchMedia("(display-mode: standalone)");
    standaloneQuery.addEventListener("change", sync);
    window.addEventListener("pageshow", sync);
    return () => {
      unsubscribe();
      standaloneQuery.removeEventListener("change", sync);
      window.removeEventListener("pageshow", sync);
    };
  }, []);

  useEffect(() => {
    if (!showHelp) return;
    const onDown = (e: MouseEvent) => {
      if (helpRef.current && !helpRef.current.contains(e.target as Node)) setShowHelp(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setShowHelp(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [showHelp]);

  // Wait until the browser display mode can be checked, and never offer installation inside the app.
  if (!ready || installed) {
    return null;
  }

  const onDownload = () => {
    if (cap === "android") {
      void promptInstall("header_button");
      return;
    }
    trackInstallPrompt("shown", { surface: "header_button_help", platform: cap === "none" ? "unknown" : cap });
    setShowHelp((v) => !v);
  };

  return (
    <div className="relative" ref={helpRef}>
      <button
        type="button"
        onClick={onDownload}
        aria-label="Download the TruWe app"
        className="hidden h-9 items-center gap-1.5 rounded-full bg-[#F2F3F5] px-3.5 text-[12px] font-semibold text-[#2C2C2C] transition-colors hover:bg-[#E8EAEE] md:inline-flex"
      >
        <Smartphone className="h-4 w-4" strokeWidth={2} aria-hidden="true" />
        <span className="hidden xl:inline">Download app</span>
      </button>
      {/* Compact icon-only variant for small screens */}
      <button
        type="button"
        onClick={onDownload}
        aria-label="Download the TruWe app"
        className="grid h-9 w-9 place-items-center rounded-full bg-[#F2F3F5] text-[#2B2D42] transition-transform active:scale-95 md:hidden"
      >
        <Smartphone className="h-4 w-4" strokeWidth={2} aria-hidden="true" />
      </button>

      {showHelp && (
        <div
          role="dialog"
          aria-label="Add TruWe to your home screen"
          className="absolute right-0 top-full z-50 mt-2 w-72 rounded-2xl border border-border bg-card p-4 shadow-lg"
        >
          <button
            type="button"
            onClick={() => setShowHelp(false)}
            aria-label="Close"
            className="absolute right-2 top-2 rounded-full p-1 text-muted-foreground transition hover:bg-muted"
          >
            <X className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
          <p className="text-sm font-semibold text-foreground">Add TruWe to your home screen</p>
          {cap === "ios" ? (
            <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">
              Tap <Share className="inline h-3 w-3 align-[-1px]" aria-hidden="true" />{" "}
              <span className="font-medium text-foreground">Share</span> in Safari, then{" "}
              <span className="font-medium text-foreground">Add to Home Screen</span>.
            </p>
          ) : (
            <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">
              Open <span className="font-medium text-foreground">truwe.in</span> in Chrome on your
              phone, tap the <span className="font-medium text-foreground">⋮ menu</span>, then{" "}
              <span className="font-medium text-foreground">Add to Home screen</span>.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
